import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { PureComponent } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { addNetworkExperts, removeNetworkExperts } from '@/actions/internalNetwork';
import { notify } from '@/actions/ui';
import { RootState } from '@/store';
import { unique } from '@/utils';

import EditDialog from '../EditDialog/EditDialog';
import FieldContainer from '../FieldContainer/FieldContainer';
import InternalNetworkSharePromoDialog from '../InternalNetworkSharePromoDialog';
import s from './InternalNetworkShareSingleProfile.module.scss';

interface InternalNetworkShareSingleProfileProps {
  open?: boolean;
  profile: any;
  groups?: any;
  onClose?: () => void;
}

const connector = connect(
  (state: RootState) => ({
    viewer: state.viewer,
    groups: state.groups.networks,
  }),
  {
    addNetworkExperts,
    removeNetworkExperts,
    notify,
  }
);

class InternalNetworkShareSingleProfile extends PureComponent<
  InternalNetworkShareSingleProfileProps & ConnectedProps<typeof connector>
> {
  constructor(props: any) {
    super(props);

    const { expert_internal_networks: expertNetworks } = props.profile;

    const selectedNetworks = {};

    if (expertNetworks) {
      expertNetworks.forEach((n: any) => {
        if (['active', 'awaiting_acceptance'].includes(n.connection_state)) {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          selectedNetworks[n.network.id] = true;
        }
      });
    }

    this.state = {
      selectedNetworks,
      addNote: '',
      removalMessage: '',
    };
  }

  getAddedNetworks = () => {
    const { profile } = this.props;
    // @ts-expect-error TS(2339): Property 'selectedNetworks' does not exist on type... Remove this comment to see the full error message
    const { selectedNetworks } = this.state;
    const { expert_internal_networks: expertNetworks } = profile;

    const sharedNetworks = this.getSharedNetworks();

    const addedNetworks = Object.keys(selectedNetworks)
      .filter((n) => selectedNetworks[n])
      .filter(
        (n) =>
          !expertNetworks.some(
            (e: any) =>
              e.network.id === n && ['active', 'awaiting_acceptance'].includes(e.connection_state)
          )
      );

    return sharedNetworks.filter((n: any) => addedNetworks.includes(n.id));
  };

  getRemovedNetworks = () => {
    const { profile } = this.props;
    // @ts-expect-error TS(2339): Property 'selectedNetworks' does not exist on type... Remove this comment to see the full error message
    const { selectedNetworks } = this.state;
    const { expert_internal_networks: expertNetworks } = profile;

    const sharedNetworks = this.getSharedNetworks();

    const removedNetworks = Object.keys(selectedNetworks)
      .filter((n) => !selectedNetworks[n])
      .filter((n) => expertNetworks.some((e: any) => e.network.id === n));

    return sharedNetworks.filter((n: any) => removedNetworks.includes(n.id));
  };

  getRemovedNetworkExperts = () => {
    const { profile } = this.props;
    // @ts-expect-error TS(2339): Property 'selectedNetworks' does not exist on type... Remove this comment to see the full error message
    const { selectedNetworks } = this.state;
    const { expert_internal_networks: expertNetworks } = profile;

    const uncheckedNetworks = Object.keys(selectedNetworks).filter((n) => !selectedNetworks[n]);

    return expertNetworks.filter((e: any) => uncheckedNetworks.includes(e.network.id));
  };

  getSharedNetworks = () => {
    const { viewer, groups } = this.props;

    if (viewer.admin) {
      return groups.edges.map((e: any) => e.node.internal_network).filter(Boolean) || [];
    }

    const sharedNetworks = viewer.groups
      ? viewer.groups
          .map((g: any) => g.shared_internal_networks || [])
          .filter(Boolean)
          .reduce((acc: any, n: any) => [...acc, ...n])
      : [];

    return unique(sharedNetworks, (n: any) => n.id);
  };

  handleNetworkSelect = (id: any, checked: any) => {
    this.setState((prevState) => ({
      selectedNetworks: {
        // @ts-expect-error TS(2339): Property 'selectedNetworks' does not exist on type... Remove this comment to see the full error message
        ...prevState.selectedNetworks,
        [id]: checked,
      },
    }));
  };

  handleAddNoteChange = (_e: any, addNote: any) => this.setState({ addNote });

  handleRemovalMessageChange = (_e: any, removalMessage: any) => this.setState({ removalMessage });

  handleSubmit = async () => {
    const { profile, addNetworkExperts, removeNetworkExperts, onClose, notify } = this.props;

    // @ts-expect-error TS(2339): Property 'addNote' does not exist on type 'Readonl... Remove this comment to see the full error message
    const { addNote, removalMessage } = this.state;

    try {
      const removedNetworkExpert = this.getRemovedNetworkExperts();
      if (removedNetworkExpert.length > 0) {
        await removeNetworkExperts({
          networkExpertIds: removedNetworkExpert.map((e: any) => e.id),
          message: removalMessage,
          unshare: true,
        });
      }

      const addedNetworks = this.getAddedNetworks();
      if (addedNetworks.length > 0) {
        await addNetworkExperts({
          profileIds: [profile.id],
          networkIds: addedNetworks.map((n: any) => n.id),
          addMethod: 'group_share',
          teamNote: addNote,
        });
      }

      if (onClose) onClose();

      notify('Sharing settings updated.', 'success');
    } catch (err) {
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (err.isPermission) {
        return notify('Not authorized to remove expert from network.', 'error');
      }
      notify('An error occurred when updating sharing settings.', 'error');
      throw err;
    }
  };

  shouldDisableNetwork = (network: any) => {
    const { viewer, profile } = this.props;

    if (viewer.admin) return false;

    const { expert_internal_networks: expertNetworks } = profile;

    const expertNetwork = expertNetworks.find((e: any) => e.network.id === network.id);

    if (!expertNetwork) return false;

    // Expert was shared by one of the viewer's groups
    if (
      expertNetwork.participation_level === 'shared' &&
      (viewer.groups || []).some(
        (g: any) => g.id === (expertNetwork.from_group && expertNetwork.from_group.id)
      )
    ) {
      return false;
    }

    // Expert was shared by another group, access can't be revoked
    return true;
  };

  render() {
    const { open, profile, onClose } = this.props;

    // @ts-expect-error TS(2339): Property 'addNote' does not exist on type 'Readonl... Remove this comment to see the full error message
    const { addNote, removalMessage, selectedNetworks } = this.state;

    const sharedNetworks = this.getSharedNetworks();

    if (sharedNetworks.length === 0) {
      return <InternalNetworkSharePromoDialog open={!!open} onClose={onClose} />;
    }

    const addedNetworks = this.getAddedNetworks();

    const removedNetworks = this.getRemovedNetworks();

    return (
      <EditDialog
        open={open}
        user={profile}
        title={profile.name}
        subTitle="Manage Access"
        //saveLabel="Done"
        onSubmit={this.handleSubmit}
        onReset={onClose}
        onClose={onClose}
      >
        <FieldContainer
          label="Shared groups"
          className={s.sharedGroups}
          containerClassName={s.sharedGroupsContainer}
        >
          <div className={s.sharedGroupsScroll}>
            {sharedNetworks.map((n: any) => {
              const disabled = this.shouldDisableNetwork(n);

              const name = (
                <span>
                  {n.name}
                  {disabled ? (
                    <small>
                      {' '}
                      <i>(already part of this network)</i>
                    </small>
                  ) : null}
                </span>
              );

              return (
                <FormControlLabel
                  key={n.id}
                  control={
                    <Checkbox
                      disabled={disabled}
                      checked={selectedNetworks[n.id]}
                      onChange={() => {
                        this.handleNetworkSelect(n.id, !selectedNetworks[n.id]);
                      }}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                      value={n.name}
                    />
                  }
                  label={name}
                  style={{ width: '100%', marginBottom: 5 }}
                />
              );
            })}
          </div>
        </FieldContainer>
        {addedNetworks.length > 0 && (
          <FieldContainer
            className={s.message}
            label={`Message to ${addedNetworks[0].name}${
              addedNetworks.length > 1 ? ' and others' : ''
            }`}
            title={addedNetworks.map((n: any) => n.name).join(', ')}
          >
            <TextField
              id="networkShareMessage"
              placeholder="Optional share message"
              multiline
              InputProps={{ disableUnderline: true }}
              fullWidth
              value={addNote}
              // @ts-expect-error TS(2322): Type '(e: any, addNote: any) => void' is not assig... Remove this comment to see the full error message
              onChange={this.handleAddNoteChange}
            />
          </FieldContainer>
        )}
        {removedNetworks.length > 0 && (
          <FieldContainer
            className={s.message}
            label={`Message to ${removedNetworks[0].name}${
              removedNetworks.length > 1 ? ' and others' : ''
            }`}
            title={removedNetworks.map((n: any) => n.name).join(', ')}
          >
            <TextField
              id="networkRemovalMessage"
              placeholder="Optional removal message"
              multiline
              InputProps={{ disableUnderline: true }}
              fullWidth
              value={removalMessage}
              // @ts-expect-error TS(2322): Type '(e: any, removalMessage: any) => void' is no... Remove this comment to see the full error message
              onChange={this.handleRemovalMessageChange}
            />
          </FieldContainer>
        )}
      </EditDialog>
    );
  }
}

export default connector(InternalNetworkShareSingleProfile);
